import React, { ChangeEvent, useEffect, useState } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useIntl } from "react-intl";
import { Value } from "../../../Alert/Filter/CreateFilterForm";
import { useKpiCatalog } from "../../../services/Kpi";
import { FormatType, Kpi, KpiType } from "../../../Alert/Filter/FilterType";
import { greyOutTemporaryKpis } from "../../../Alert/Filter/CreateFilterKpiForm";
import { IconValueField } from "../../../Alert/Filter/KpiValueSetter";
import {
  KpiValueFieldComponent,
  kpiValueFieldSetParams,
} from "../../../Alert/Filter/KpiValueField";
import { Row } from "../../../Layout/utils";
import { useRecoilValue } from "recoil";
import { currentUserCurrencyAtom } from "../../../AuthProvider";
import { Operator, OPERATORS } from "../../../constants";
import { formInputsStyles } from "../../styles";
import { CustomAutocomplete } from "../../../components/Inputs/CustomAutocomplete";

const useStyles = makeStyles()((theme) => ({
  operator: {
    "& .MuiFormLabel-root": {
      overflow: "initial",
    },
    "& .MuiSelect-select": {
      padding: "7.5px 14px",
    },
  },
  popperWarning: {
    fontSize: "12px",
    padding: "5px 10px",
    margin: 0,
    color: theme.vars.palette.primary.main,
  },
  paper: {
    "& .MuiAutocomplete-listbox": {
      maxHeight: "28vh",
    },
  },
}));

export type KpiSelected = Value<Kpi, false>;

export const KpiField = ({
  kpiTypeId,
  onChange,
}: {
  kpiTypeId: number;
  onChange: (value: Kpi) => void;
}) => {
  const inputStyles = formInputsStyles();
  const [inputvalue, setInputValue] = useState(null);

  const { data: catalog } = useKpiCatalog();
  const data = catalog?.types
    .find((type) => type.id === kpiTypeId)
    ?.children?.filter((el) => el.label);

  const onChangeHandler = (_event: React.ChangeEvent<{}>, value: any) => {
    setInputValue(value);
    onChange(value);
  };

  useEffect(() => {
    setInputValue(null);
  }, [kpiTypeId]);

  return (
    <Box
      className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
    >
      <CustomAutocomplete
        onChange={onChangeHandler}
        fullWidth
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => greyOutTemporaryKpis.includes(option.id)}
        label="KPI"
        loading={!data}
        options={data || []}
        value={inputvalue}
      />
    </Box>
  );
};

export type KpiTypeFieldParams = {
  kpiType: KpiType | null;
  kpi: Kpi | null;
  kpiValue: number | null;
  kpiOperator: string | null;
};

/**
 * Kpi fields.
 */
export const KpiTypeField = ({
  onChange,
}: {
  onChange: (params: KpiTypeFieldParams) => void;
}) => {
  const intl = useIntl();
  const { classes } = useStyles();
  const inputStyles = formInputsStyles();
  const currency = useRecoilValue(currentUserCurrencyAtom);
  const [kpiType, setKpiType] = useState<KpiType | null>(null);
  const [kpi, setKpi] = useState<KpiSelected | null>(null);
  const [kpiValue, setKpiValue] = useState<number | null>(null);
  const [kpiOperator, setKpiOperator] = useState<string>(
    OPERATORS[0]?.name ?? Operator.GT
  );

  const query = useKpiCatalog();

  /**
   * On change, call onChange function.
   */
  useEffect(() => {
    if (kpiType && kpi && kpiValue && kpiOperator) {
      onChange({
        kpiType,
        kpi,
        kpiValue,
        kpiOperator,
      });
    } else {
      onChange({
        kpiType: null,
        kpi: null,
        kpiValue: null,
        kpiOperator: null,
      });
    }
  }, [kpiType, kpi, kpiValue, kpiOperator, onChange]);

  /**
   * On KPI type change.
   */
  const onChangeKpiType = (
    _event: ChangeEvent<{}>,
    value: Value<KpiType, false>
  ): void => {
    setKpiType(value);
    setKpi(null);
    setKpiValue(null);
  };

  /**
   * On KPI type change.
   */
  const onChangeKpi = (value: Kpi): void => {
    setKpi(value);
    setKpiValue(null);
    setKpiOperator(OPERATORS[0]?.name ?? Operator.GT);
  };

  /**
   * On KPI value change.
   */
  const onChangeKpiValue = (value: number): void => {
    setKpiValue(value);
  };

  /**
   * On KPI operator change.
   */
  const onChangeKpiOperator = (value: string): void => {
    setKpiOperator(value);
  };

  return (
    <Box className={inputStyles.classes.root} sx={{ display: "flex" }}>
      <Box
        className={`${inputStyles.classes.container} ${inputStyles.classes.inputSize}`}
      >
        <CustomAutocomplete
          multiple={false}
          onChange={onChangeKpiType}
          fullWidth
          isOptionEqualToValue={(option: KpiType, value: KpiType) =>
            option.id === value.id
          }
          getOptionLabel={(option) => option.label}
          label={intl.formatMessage({ id: "KPI_TYPE" })}
          options={query.data?.types || []}
        />
      </Box>
      {kpiType && <KpiField kpiTypeId={kpiType.id} onChange={onChangeKpi} />}
      {kpi && (
        <Row
          className={inputStyles.classes.container}
          sx={{
            gap: 1,
            width: "100%",
          }}
        >
          {kpi.format.type === FormatType.ICON && <IconValueField kpi={kpi} />}
          {kpi.format.type !== FormatType.ICON && (
            <>
              <div style={{ flex: "0 0 64px" }}>
                <TextField
                  className={`${inputStyles.classes.inputText} ${classes.operator}`}
                  variant="outlined"
                  fullWidth
                  label={intl.formatMessage({ id: "OPERATOR" })}
                  select
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onChangeKpiOperator(event.target.value)
                  }
                  value={kpiOperator}
                >
                  {OPERATORS.map((op) => (
                    <MenuItem key={op.name} value={op.name}>
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <Box
                sx={{
                  flex: "auto",
                }}
              >
                <KpiValueFieldComponent
                  params={kpiValueFieldSetParams(kpi, currency)}
                  onChange={onChangeKpiValue}
                />
              </Box>
            </>
          )}
        </Row>
      )}
    </Box>
  );
};
